import { useState, useEffect, useCallback } from 'react';
import { Users as UsersIcon, Plus, Trash2, Shield, Upload, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

const AddUserForm = ({ onAdded, onClose }) => {
  const [form, setForm] = useState({ name: '', email: '', role: 'user', can_upload: false });
  const [saving, setSaving] = useState(false);

  const addUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/users`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(form) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || 'Could not add user'); return; }
      toast.success(`${form.name} added — they can now log in with their email`);
      onAdded();
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={addUser} className="bg-white border border-gray-200 rounded-xl p-4 mb-4 space-y-3" data-testid="add-user-form">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold text-gray-700">New User</span>
        <button type="button" onClick={onClose} className="text-gray-300 hover:text-gray-500"><X size={14} /></button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] font-semibold text-gray-500 block mb-1">Full Name *</label>
          <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Jane Smith" className="h-8 text-xs" data-testid="add-user-name" />
        </div>
        <div>
          <label className="text-[10px] font-semibold text-gray-500 block mb-1">Email *</label>
          <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="jane@company.com" className="h-8 text-xs" data-testid="add-user-email" />
        </div>
      </div>
      <div className="flex items-center gap-5">
        <label className="flex items-center gap-1.5 text-xs text-gray-600">
          <input type="checkbox" checked={form.role === 'admin'} onChange={e => setForm(f => ({ ...f, role: e.target.checked ? 'admin' : 'user' }))} data-testid="add-user-admin" />
          Admin (full access)
        </label>
        <label className="flex items-center gap-1.5 text-xs text-gray-600">
          <input type="checkbox" checked={form.can_upload} disabled={form.role === 'admin'} onChange={e => setForm(f => ({ ...f, can_upload: e.target.checked }))} data-testid="add-user-canupload" />
          Can upload guidelines
        </label>
      </div>
      <Button type="submit" disabled={saving || !form.name.trim() || !form.email.trim()} size="sm" className="h-8 text-xs bg-[#101C47]" data-testid="add-user-submit">
        {saving ? 'Adding…' : 'Add User'}
      </Button>
    </form>
  );
};

const UserRow = ({ u, isSelf, onUpdate, onDelete }) => (
  <div className={`flex items-center gap-3 bg-white border rounded-xl px-4 py-3 ${u.is_active ? 'border-gray-200' : 'border-gray-100 opacity-60'}`} data-testid={`user-row-${u.id}`}>
    <div className="w-8 h-8 rounded-full bg-[#101C47]/5 flex items-center justify-center text-xs font-bold text-[#101C47] flex-shrink-0">
      {(u.name || u.email || '?').charAt(0).toUpperCase()}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-bold text-gray-800">{u.name}</span>
        {u.role === 'admin' && <span className="text-[8px] font-bold bg-[#101C47] text-white px-1.5 py-0.5 rounded flex items-center gap-0.5"><Shield size={7} /> ADMIN</span>}
        {u.role !== 'admin' && u.can_upload && <span className="text-[8px] font-bold bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded flex items-center gap-0.5"><Upload size={7} /> CAN UPLOAD</span>}
        {!u.is_active && <span className="text-[8px] font-bold bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">DEACTIVATED</span>}
        {isSelf && <span className="text-[8px] font-bold bg-green-100 text-green-600 px-1.5 py-0.5 rounded">YOU</span>}
      </div>
      <p className="text-[11px] text-gray-400 truncate">{u.email}</p>
    </div>
    {!isSelf && (
      <div className="flex items-center gap-1">
        {u.role !== 'admin' && (
          <button onClick={() => onUpdate(u.id, { can_upload: !u.can_upload }, u.can_upload ? 'Upload access revoked' : 'Upload access granted')}
            className="text-[10px] text-gray-400 hover:text-blue-600 px-2 py-1 rounded hover:bg-blue-50" data-testid={`toggle-upload-${u.id}`}>
            {u.can_upload ? 'Revoke upload' : 'Allow upload'}
          </button>
        )}
        <button onClick={() => onUpdate(u.id, { role: u.role === 'admin' ? 'user' : 'admin' }, 'Role updated')}
          className="text-[10px] text-gray-400 hover:text-[#101C47] px-2 py-1 rounded hover:bg-gray-50" data-testid={`toggle-role-${u.id}`}>
          {u.role === 'admin' ? 'Make user' : 'Make admin'}
        </button>
        <button onClick={() => onUpdate(u.id, { is_active: !u.is_active }, u.is_active ? 'User deactivated' : 'User reactivated')}
          className="text-[10px] text-gray-400 hover:text-amber-600 px-2 py-1 rounded hover:bg-amber-50" data-testid={`toggle-active-${u.id}`}>
          {u.is_active ? 'Deactivate' : 'Reactivate'}
        </button>
        <button onClick={() => onDelete(u)} className="text-gray-200 hover:text-red-400 p-1.5 rounded hover:bg-red-50" data-testid={`delete-user-${u.id}`}>
          <Trash2 size={12} />
        </button>
      </div>
    )}
  </div>
);

export default function UsersPage({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    const res = await fetch(`${API}/api/users`, { headers: authHeaders() });
    if (res.ok) setUsers((await res.json()).users || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateUser = async (id, updates, okMsg) => {
    const res = await fetch(`${API}/api/users/${id}`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify(updates) });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { toast.error(data.detail || 'Update failed'); return; }
    toast.success(okMsg || 'User updated');
    load();
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.name || u.email}? They will no longer be able to log in.`)) return;
    const res = await fetch(`${API}/api/users/${u.id}`, { method: 'DELETE', headers: authHeaders() });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { toast.error(data.detail || 'Delete failed'); return; }
    toast.success('User deleted');
    load();
  };

  return (
    <div className="h-full overflow-y-auto p-6" data-testid="users-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#101C47] flex items-center justify-center"><UsersIcon size={16} className="text-white" /></div>
            <div>
              <h1 className="text-base font-bold text-gray-900">User Management</h1>
              <p className="text-[10px] text-gray-400">Users sign in with a one-time code emailed to them — no passwords</p>
            </div>
          </div>
          <Button size="sm" onClick={() => setShowAdd(true)} className="h-8 text-xs bg-[#101C47] gap-1" data-testid="add-user-btn">
            <Plus size={12} /> Add User
          </Button>
        </div>

        {showAdd && <AddUserForm onAdded={() => { setShowAdd(false); load(); }} onClose={() => setShowAdd(false)} />}

        <div className="space-y-2" data-testid="users-list">
          {users.map(u => (
            <UserRow key={u.id} u={u} isSelf={u.id === currentUser.id} onUpdate={updateUser} onDelete={deleteUser} />
          ))}
        </div>
      </div>
    </div>
  );
}
